<template>
  <div :class="$style.wrapper">
    <div :class="$style.tools">
      <div :class="$style.toolWrapper">
        <InputSearch
          :class="$style.search"
          data-test-id="CloudInsightsItemListToolbar__search"
          placeholder="search"
          :value="getSearchTerm"
          @input="handleSearch"
        />
        <Checkbox
          :class="$style.filterCheckbox"
          data-test-id="CloudInsightsItemListToolbar__filterCheckbox"
          label="Filter out non-person entity events"
          :value="nonPersonEntityFilter"
          @input="handleNpeChange"
        />
      </div>
      <div :class="$style.manage">
        <DropdownActions
          :class="$style.exportBtn"
          :items="exportOptions"
        >
          <template v-slot:button="dropdown">
            <IconButton
              v-bind="dropdown.defaults"
              data-test-id="CloudInsightsItemListToolbar__export"
              :hasNoBackground="true"
              icon="caretDown"
              iconPosition="right"
              :scale="0.5"
              text="Export"
              type="informational"
              @click="dropdown.defaults.click()"
            />
          </template>
        </DropdownActions>
        <IconButton
          data-test-id="CloudInsightsItemListToolbar__manageAccounts"
          hasOutline
          icon="settings"
          text="Manage Accounts"
          type="secondary"
          @click="handleManageAccountsClick"
        />
      </div>
    </div>
    <div :class="$style.tools">
      <div :class="$style.toolWrapper">
        <DropDownMultiSelectSearch
          :class="$style.columnSelector"
          data-test-id="CloudInsightsItemListToolbar__columns"
          :disabled="totalEvents <= 0"
          :displayValue="false"
          :maxOptionsSelectable="8"
          name="Columns"
          :options="columnOptions"
          :sortOptions="false"
          :useDarkLinkStyleForButton="true"
          :value="selectedColumnStrings"
          @input="handleColumnsChange"
        >
          <template v-slot:footer>
            <div>
              <a
                :class="$style.revertColumnsLink"
                @click="revertToDefaultColumns"
              >
                Revert to default columns
              </a>
            </div>
          </template>
        </DropDownMultiSelectSearch>
      </div>
      <DatePickerRangeInput
        data-test-id="CloudInsightsItemListToolbar__datePicker"
        :preventFutureDates="true"
        showTimePicker
        :value="dateTimeRangeValue"
        @input="handleDateTimeChange"
      />
    </div>
    <ProgressModal
      :canCancel="true"
      data-test-id="CloudInsightsItemListToolbar__progressModule"
      headerText="Exporting Cloud Insights Events..."
      mainActionText="cancel export"
      :showCount="false"
      @cancel="cancelExport"
    />
  </div>
</template>

<script>
import {
  Checkbox,
  DatePickerRangeInput,
  DropdownActions,
  IconButton,
  InputSearch,
  Types,
} from '@jumpcloud/ui-components';
import {
  ExportFileTypes,
  ItemTypeConsts,
  LocalStorageConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  defaultColumnsData,
  eventFields,
  routeConsts,
  routeNames,
} from 'client/CloudInsights/constants/CloudInsightsConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import DropDownMultiSelectSearch from 'components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import ProgressModal from 'components/Modal/ProgressModal.vue';

const { cloudInsightsEventsSelectedColumns } = LocalStorageConsts;
const { DropdownMenuItem } = Types;
const cloudInsightsEventsModule = StoreModuleMap[ItemTypeConsts.cloudInsightsEvents];
const defaultColumnNames = defaultColumnsData.map(columnConfig => columnConfig.label);

export default {
  name: 'CloudInsightsItemListToolbar',

  components: {
    Checkbox,
    DatePickerRangeInput,
    DropdownActions,
    DropDownMultiSelectSearch,
    IconButton,
    InputSearch,
    ProgressModal,
  },

  LocalStorageService,

  props: {
    rowsPerPage: {
      type: Number,
      default: 10,
    },
    selectedColumns: {
      type: Array,
      default: () => ([]),
    },
    totalEvents: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    ...mapGetters(cloudInsightsEventsModule, [
      'getCollection',
      'getDateTimeRange',
      'getSearchTerm',
    ]),

    ...mapState(cloudInsightsEventsModule, [
      'nonPersonEntityFilter',
    ]),

    columnOptions() {
      const requiredFields = ['Date'];
      return Object.values(eventFields)
        .sort()
        .filter(field => (field !== '')) // Remove collapse column
        .map(field => ({
          name: field,
          value: field,
          required: requiredFields.includes(field),
        }));
    },

    dateTimeRangeValue() {
      return this.getDateTimeRange;
    },

    exportOptions() {
      return [
        DropdownMenuItem({
          click: () => this.exportData(ExportFileTypes.json),
          text: 'Export JSON',
        }),
        DropdownMenuItem({
          click: () => this.exportData(ExportFileTypes.csv),
          text: 'Export CSV',
        }),
      ];
    },

    selectedColumnStrings() {
      return this.selectedColumns
        .filter((col) => (col.label !== '')) // Remove collapse column
        .map((col) => (col.label));
    },
  },

  methods: {
    ...mapActions(cloudInsightsEventsModule, [
      'cancelExport',
      'exportData',
      'fetchCollection',
      'setDateTimeRange',
      'setLoadingState',
      'setNonPersonEntityFilter',
      'setPage',
      'setSearchTerm',
      'resetCollection',
    ]),

    handleColumnsChange(selectedColumns) {
      const { setItem } = this.$options.LocalStorageService;
      // Add collapse column back to array
      selectedColumns.unshift('');
      setItem(cloudInsightsEventsSelectedColumns, JSON.stringify(selectedColumns));
      this.$emit('columnsChange');
    },

    async handleDateTimeChange(dateTimeRange) {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });
      this.setDateTimeRange({ dateTimeRange });
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
    },

    handleManageAccountsClick() {
      this.$router.push({ name: routeNames[routeConsts.cloudInsightsAccounts] });
    },

    async handleNpeChange(value) {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });
      this.setNonPersonEntityFilter({ value });
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
    },

    async handleSearch(searchTerm) {
      this.$emit('search', searchTerm);
    },

    revertToDefaultColumns() {
      const { setItem } = this.$options.LocalStorageService;
      setItem(cloudInsightsEventsSelectedColumns, JSON.stringify(defaultColumnNames));
      this.$emit('columnsChange');
    },
  },
};
</script>

<style module scoped>
.columnSelector {
  border: 2px solid var(--jcBlack);
  border-radius: var(--jc-border-radius-small);
  cursor: pointer;
}

.columnSelector /deep/ button {
  color: black;
  height: 26px;
  padding: 4px 8px;
  width: 90px;
}

.columnSelector:hover {
  background-color: var(--jcGrey200);
}

.exportBtn {
  margin-right: 30px;
}

.exportBtn /deep/ ul {
  width: 135px;
}

.manage {
  align-items: center;
  display: inline-flex;
}

.revertColumnsLink { cursor: pointer; }

.search {
  padding-right: 16px;
  width: 320px;
}

.toolWrapper {
  align-items: center;
  display: inline-flex;
}

.tools {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
}

.tools:first-child {
  padding-bottom: 16px;
}

.wrapper {
  padding: 16px 16px 0 0;
}
</style>
